import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  permissions?: string[];
  isVerified: boolean;
  isActive?: boolean;
}

interface DecodedToken {
  id: number;
  email: string;
  role: string;
  exp: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: (logoutAll?: boolean) => Promise<void>;
  refreshToken: () => Promise<boolean>;
  updateProfile: (userData: { firstName?: string; lastName?: string }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if token is valid
  const isTokenValid = (token: string) => {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (error) {
      return false;
    }
  };

  // Set token and update localStorage (interceptor will handle headers)
  const setAuthToken = (newToken: string | null) => {
    setToken(newToken);
    if (newToken) {
      localStorage.setItem('token', newToken);
    } else {
      localStorage.removeItem('token');
    }
  };

  // Refresh access token
  const refreshToken = async (): Promise<boolean> => {
    try {
      const response = await api.post('/auth/refresh-token');
      const { accessToken } = response.data.data;
      
      setAuthToken(accessToken);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      await logout();
      return false;
    }
  };

  // Load user from localStorage and verify token on initial render
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        if (isTokenValid(storedToken)) {
          setAuthToken(storedToken);
          setUser(JSON.parse(storedUser));
          
          // Try to get fresh user data
          try {
            const response = await api.get('/auth/profile');
            const userData = response.data.data;
            const userWithPermissions = {
              ...userData,
              firstName: userData.firstName,
              lastName: userData.lastName,
              isVerified: userData.isVerified
            };
            setUser(userWithPermissions);
            localStorage.setItem('user', JSON.stringify(userWithPermissions));
          } catch (error) {
            console.error('Failed to fetch user profile:', error);
          }
        } else {
          // Token expired, try to refresh
          const refreshed = await refreshToken();
          if (!refreshed) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }
      }

      setIsLoading(false);
    };

    loadUser();
  }, []);

  // Login user
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      const { accessToken, user: userData } = response.data.data;

      setAuthToken(accessToken);
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };


  // Logout user
  const logout = async (logoutAll: boolean = false) => {
    try {
      if (logoutAll) {
        await api.post('/auth/logout-all');
      } else {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      setAuthToken(null);
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  // Update user profile
  const updateProfile = async (userData: { firstName?: string; lastName?: string }) => {
    setIsLoading(true);
    try {
      const response = await api.put('/auth/profile', userData);
      const updatedUser = response.data.data;

      const updatedUserData = { 
        ...user, 
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName
      };
      setUser(updatedUserData as User);
      localStorage.setItem('user', JSON.stringify(updatedUserData));
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Change password
  const changePassword = async (currentPassword: string, newPassword: string) => {
    setIsLoading(true);
    try {
      await api.put('/auth/change-password', { 
        currentPassword, 
        newPassword 
      });
      
      // Password change forces logout, so clear local state
      setAuthToken(null);
      setUser(null);
      localStorage.removeItem('user');
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user has specific permission
  const hasPermission = (permission: string): boolean => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  // Check if user has specific role(s)
  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        isLoading,
        login,
        logout,
        refreshToken,
        updateProfile,
        changePassword,
        hasPermission,
        hasRole
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
